import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { SN_Navbar } from "@/components/SN_Navbar";
import { SndBar, SndFooter } from "@/platform/chrome";
import { Home } from "@/platform/Home";
import { GREEKSOUP, SITE } from "@/content/datasets";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/useSubscription";
import { ClubSubscribeModal as SubscribeModal } from "@/components/club/ClubCheckout";
import { ClubFoundingModal as FoundingMemberModal } from "@/components/club/ClubCheckout";

const TITLE = "Company Fundamental Data for Stock Research | Shikshan Nivesh";
const DESCRIPTION =
  "Company fundamental data for US stocks: results and financial statements, insider trades, fund holdings, Congress trades and short interest, updated every business day.";

// The Club's pricing and checkout live on its own page; the Data plans show on the home page (30 Oct 2026).

const organizationSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "Shikshan Nivesh",
      legalName: "Borkar Nexus Private Limited",
      url: SITE,
      logo: { "@type": "ImageObject", url: `${SITE}/android-chrome-512x512.png`, width: 512, height: 512 },
      description:
        "Shikshan Nivesh publishes company fundamental data for stock research on US companies, built from their SEC filings and the official records of the market, and makes greeksoup.ai, the Alpha with AI newsletter and the Alpha with AI Club.",
      slogan: "Run institutional-grade research. Without the institution.",
      foundingDate: "2025",
      identifier: { "@type": "PropertyValue", name: "CIN", value: "U85500PN2025PTC243915" },
      founder: { "@type": "Person", "@id": `${SITE}/#founder`, name: "Shubham Borkar", url: `${SITE}/shubham-borkar` },
      address: { "@type": "PostalAddress", addressCountry: "IN", addressRegion: "Maharashtra", addressLocality: "Pune" },
      email: "info@shikshannivesh.com",
      knowsAbout: [
        "Company fundamental data",
        "Financial statements",
        "SEC filings",
        "Insider trading data",
        "13F fund holdings",
        "Congressional stock trades",
        "Short interest",
        "Stock research",
      ],
      sameAs: [GREEKSOUP, "https://ai.shikshannivesh.com", "https://www.linkedin.com/company/shikshan-nivesh"],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE}/#website`,
      url: SITE,
      name: "Shikshan Nivesh",
      publisher: { "@id": `${SITE}/#organization` },
    },
  ],
};

const Index = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { isSubscribed, isLoading: subLoading } = useSubscription(user);
  const [autoSubscribeOpen, setAutoSubscribeOpen] = useState(false);
  const [autoFoundingOpen, setAutoFoundingOpen] = useState(false);

  // If a logged-out user clicked "Start membership", they returned here with
  // ?subscribe=1. Once auth + subscription state are resolved, auto-open the
  // subscribe modal for non-members and strip the query param.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribe") !== "1") return;
    if (authLoading || (user && subLoading)) return;

    if (user && !isSubscribed) {
      setAutoSubscribeOpen(true);
    }

    params.delete("subscribe");
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }, [authLoading, subLoading, user, isSubscribed]);

  // ?founding=1 deep-link: open Founding Member modal once auth resolves.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("founding") !== "1") return;
    if (authLoading) return;
    if (user) setAutoFoundingOpen(true);
    params.delete("founding");
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }, [authLoading, user]);

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={`${SITE}/`} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={`${SITE}/`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <SN_Navbar />
        <main>
          {/* The home page v5 (his go, 30 Oct 2026): the record of any company, the latest filings, what is
              in the data, the spreadsheet and the API, the plans. /data keeps the market board. */}
          <div className="pt-[56px] lg:pt-[60px]">
            <SndBar />
            <Home />
          </div>
        </main>
        <SndFooter />
      </div>

      {/* Kept on purpose: Club and Founding sign-ups return here with ?subscribe=1 / ?founding=1. Both open through the Club gate (ClubCheckout). */}
      <SubscribeModal isOpen={autoSubscribeOpen} onClose={() => setAutoSubscribeOpen(false)} />
      <FoundingMemberModal isOpen={autoFoundingOpen} onClose={() => setAutoFoundingOpen(false)} />
    </>
  );
};

export default Index;
